import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { type DeliveryMode } from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

export function getDeliveryModeDisplayName(deliveryMode: DeliveryMode): string {
    switch (deliveryMode) {
        case 'sms':
            return 'SMS';
        case 'voice':
            return 'Phone';
        case 'chat':
            return 'Chat';
        case 'email':
            return 'Email';
        default:
            assert.never(deliveryMode);
    }
}

/**
 * Formats a contact attempt status for display
 * @param status - The raw status string (e.g., 'initiated', 'sent', 'completed')
 * @returns The formatted status name for display
 */
export function getContactAttemptStatusDisplayName(status: string): string {
    // Convert to title case with special handling for known statuses
    const words = status.toLowerCase().split('_');
    return words
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export function isS3RecordingUrl(recordingUrl: string | null): boolean {
    if (!recordingUrl) {
        return false;
    }

    return (
        recordingUrl.includes('s3.amazonaws.com') ||
        recordingUrl.startsWith('s3://')
    );
}

// (XXX) XXX-XXXX for North American numbers; other countries keep their
// international format so the country is never dropped or misread.
export function formatNationalPhoneNumber(
    value: string | null | undefined,
): string | null {
    if (!value) {
        return null;
    }

    const trimmed = value.trim();
    const parsed = parsePhoneNumberFromString(trimmed, 'US');
    if (parsed?.isPossible()) {
        return parsed.countryCallingCode === '1'
            ? parsed.formatNational()
            : parsed.formatInternational();
    }

    if (trimmed.startsWith('+')) {
        return trimmed;
    }

    return formatPhoneNumber(trimmed, { includeCountryPrefix: false });
}

export function formatPhoneNumber(
    value: string | null,
    { includeCountryPrefix = true }: { includeCountryPrefix?: boolean } = {},
): string | null {
    if (!value) {
        return null;
    }

    const trimmed = value.trim();
    const digits = trimmed.replace(/\D/g, '');
    const nationalDigits =
        digits.length === 11 && digits.startsWith('1')
            ? digits.slice(1)
            : digits;

    if (nationalDigits.length !== 10) {
        return trimmed;
    }

    const countryPrefix =
        includeCountryPrefix && digits.length === 11 && digits.startsWith('1')
            ? '+1 '
            : '';

    return `${countryPrefix}(${nationalDigits.slice(0, 3)}) ${nationalDigits.slice(
        3,
        6,
    )}-${nationalDigits.slice(6)}`;
}
